import { useState, type ComponentType } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  BookMarked,
  BookOpenText,
  ChartNoAxesColumn,
  CircleUserRound,
  History,
  LayoutGrid,
  ListChecks,
  LogOut,
  Map as MapIcon,
  Menu,
  PenLine,
  Target,
  Users,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { Dialog, SheetContent } from '@/components/ui/dialog';

type Item = { to: string; label: string; icon: ComponentType<{ className?: string }>; end?: boolean };

const GROUPS: { title: string; items: Item[] }[] = [
  {
    title: 'Lernen',
    items: [
      { to: '/', label: 'Übersicht', icon: LayoutGrid, end: true },
      { to: '/lernpfad', label: 'Lernpfad', icon: MapIcon },
      { to: '/training', label: 'Heute lernen', icon: ListChecks },
      { to: '/lesen', label: 'Leseverstehen', icon: BookOpenText },
      { to: '/schreiben', label: 'Schreibtrainer', icon: PenLine },
    ],
  },
  {
    title: 'Wiederholen',
    items: [
      { to: '/fehler', label: 'Meine Fehler', icon: Target },
      { to: '/glossar', label: 'Glossar', icon: BookMarked },
    ],
  },
  {
    title: 'Auswertung',
    items: [
      { to: '/fortschritt', label: 'Fortschritt', icon: ChartNoAxesColumn },
      { to: '/verlauf', label: 'Verlauf', icon: History },
    ],
  },
];

const MOBILE_TABS: Item[] = [
  { to: '/', label: 'Übersicht', icon: LayoutGrid, end: true },
  { to: '/lernpfad', label: 'Lernpfad', icon: MapIcon },
  { to: '/training', label: 'Heute', icon: ListChecks },
  { to: '/fehler', label: 'Fehler', icon: Target },
  { to: '/fortschritt', label: 'Fortschritt', icon: ChartNoAxesColumn },
];

function Brand() {
  return (
    <div>
      <div className="text-[10px] font-bold tracking-[.32em] text-wine">DEUTSCH · A2 → B2</div>
      <div className="font-serif text-[26px] leading-tight">Mayras Lernraum.</div>
    </div>
  );
}

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  const { user } = useAuth();
  const groups: { title: string; items: Item[] }[] =
    user?.role === 'OWNER'
      ? [...GROUPS, { title: 'Verwaltung', items: [{ to: '/nutzer', label: 'Nutzer', icon: Users }] }]
      : GROUPS;
  return (
    <nav className="space-y-6" aria-label="Hauptnavigation">
      {groups.map((g) => (
        <div key={g.title}>
          <div className="mb-2 px-3 text-[10px] font-bold uppercase tracking-label text-ink-muted">{g.title}</div>
          <ul className="space-y-0.5">
            {g.items.map(({ to, label, icon: Icon, end }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={end}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-xl px-3 py-2 text-[14px] font-medium transition',
                      isActive ? 'bg-espresso text-cream' : 'text-ink-soft hover:bg-pink-soft/60 hover:text-ink',
                    )
                  }
                >
                  <Icon className="h-[18px] w-[18px] shrink-0" />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function UserBox({ onNavigate }: { onNavigate?: () => void }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  if (!user) return null;
  return (
    <div className="border-t border-line pt-4">
      <NavLink
        to="/konto"
        onClick={onNavigate}
        className="flex items-center gap-3 rounded-xl px-3 py-2 transition hover:bg-pink-soft/60"
      >
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-pink font-serif text-lg text-espresso">
          {user.name.slice(0, 1).toUpperCase()}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">{user.name}</span>
          <span className="block text-[11px] uppercase tracking-wider text-ink-muted">
            {user.role === 'OWNER' ? 'Owner' : 'Student'}
          </span>
        </span>
        <CircleUserRound className="ml-auto h-4 w-4 text-ink-muted" />
      </NavLink>
      <button
        onClick={async () => {
          await logout();
          qc.clear();
          navigate('/login');
        }}
        className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm text-ink-soft transition hover:bg-pink-soft/60 hover:text-ink"
      >
        <LogOut className="h-4 w-4" /> Abmelden
      </button>
    </div>
  );
}

export function AppShell() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const focusMode = location.pathname.startsWith('/training/sitzung');

  return (
    <div className="min-h-screen bg-cream text-ink">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-full focus:bg-espresso focus:px-4 focus:py-2 focus:text-cream">
        Zum Inhalt springen
      </a>

      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-line/80 bg-cream px-4 pb-4 pt-7 lg:flex">
        <div className="px-3">
          <Brand />
        </div>
        <div className="mt-9 flex-1 overflow-y-auto">
          <NavItems />
        </div>
        <UserBox />
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-line/80 bg-cream/95 px-4 py-3 backdrop-blur lg:hidden">
        <Brand />
        <Dialog open={open} onOpenChange={setOpen}>
          <button
            onClick={() => setOpen(true)}
            className="grid h-10 w-10 place-items-center rounded-full border border-line bg-paper"
            aria-label="Menü öffnen"
          >
            <Menu className="h-5 w-5" />
          </button>
          <SheetContent title="Menü">
            <div className="flex h-full flex-col px-4 pb-4 pt-6">
              <div className="px-3">
                <Brand />
              </div>
              <div className="mt-8 flex-1 overflow-y-auto">
                <NavItems onNavigate={() => setOpen(false)} />
              </div>
              <UserBox onNavigate={() => setOpen(false)} />
            </div>
          </SheetContent>
        </Dialog>
      </header>

      <main id="main" className={cn('lg:pl-64', !focusMode && 'pb-24 lg:pb-0')}>
        <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 lg:px-10 lg:py-12">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom tabs */}
      {!focusMode && (
        <nav
          className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-cream/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden"
          aria-label="Schnellnavigation"
        >
          <ul className="mx-auto grid max-w-md grid-cols-5">
            {MOBILE_TABS.map(({ to, label, icon: Icon, end }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    cn(
                      'flex flex-col items-center gap-1 py-2.5 text-[10.5px] font-semibold',
                      isActive ? 'text-wine' : 'text-ink-muted',
                    )
                  }
                >
                  <Icon className="h-5 w-5" />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}
